import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  TablePagination,
  Divider,
  TextField,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { financeService } from '../services/financeService';
import { useNotification } from '../contexts/NotificationContext';

const statusChip = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return { label: 'Aprovada', color: 'success', variant: 'filled' };
  if (s === 'rejected') return { label: 'Recusada', color: 'error', variant: 'filled' };
  return { label: 'Pendente', color: 'warning', variant: 'outlined' };
};

const labelForField = (field) => {
  const map = {
    name: 'Nome',
    description: 'Descrição',
    category: 'Categoria',
    brand: 'Marca',
    model: 'Modelo',
    year: 'Ano',
    location: 'Localização',
    notes: 'Observações',
    product_id: 'Produto/Sorteio',
    bank_id: 'Banco'
  };
  return map[field] || field;
};

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

const AssetEditRequests = () => {
  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const offset = useMemo(() => page * rowsPerPage, [page, rowsPerPage]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeService.getAssetEditRequestsPage({
        offset,
        limit: rowsPerPage
      });
      setItems(res?.data || []);
      setTotal(Number(res?.total || 0));
    } catch (e) {
      setError(e?.message || 'Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, rowsPerPage]);

  const openDetail = async (row) => {
    setSelected(row);
    setReviewNotes('');
    setDetailOpen(true);

    try {
      const full = await financeService.getAssetEditRequest(row.id);
      setSelected(full);
    } catch (e) {
      // Fallback: keep row.
    }
  };

  const closeDetail = () => {
    if (submitting) return;
    setDetailOpen(false);
    setSelected(null);
    setReviewNotes('');
  };

  const handleApprove = async () => {
    if (!selected?.id || submitting) return;
    setSubmitting(true);
    try {
      await financeService.approveAssetEditRequest(selected.id, { notes: reviewNotes?.trim() || null });
      showNotification('Solicitação aprovada com sucesso', 'success');
      closeDetail();
      await loadPage();
    } catch (e) {
      showNotification(e?.message || 'Erro ao aprovar solicitação', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selected?.id || submitting) return;
    setSubmitting(true);
    try {
      await financeService.rejectAssetEditRequest(selected.id, { notes: reviewNotes?.trim() || null });
      showNotification('Solicitação recusada com sucesso', 'success');
      closeDetail();
      await loadPage();
    } catch (e) {
      showNotification(e?.message || 'Erro ao recusar solicitação', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const changesEntries = useMemo(() => {
    const changes = selected?.changes || {};
    return Object.entries(changes);
  }, [selected]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight="bold">
          Solicitações de Alteração de Ativos
        </Typography>
        <Tooltip title="Recarregar">
          <span>
            <IconButton onClick={loadPage} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Solicitações <strong>aprovadas</strong> ou <strong>recusadas</strong> são removidas automaticamente do sistema após <strong>60 dias</strong>.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Ativo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Solicitante</TableCell>
                    <TableCell>Revisor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => {
                    const chip = statusChip(row.status);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {row.requested_at ? new Date(row.requested_at).toLocaleString('pt-BR') : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {row.asset_name || row.asset_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            variant={chip.variant}
                          />
                        </TableCell>
                        <TableCell>{row.requested_by_name || row.requested_by || '—'}</TableCell>
                        <TableCell>{row.reviewed_by_name || row.reviewed_by || '—'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalhes">
                            <IconButton size="small" onClick={() => openDetail(row)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box py={4} color="text.secondary">
                          Nenhuma solicitação encontrada.
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas por página"
            />
          </>
        )}
      </Paper>

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes da Solicitação</DialogTitle>
        <DialogContent dividers>
          {!selected ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Ativo</Typography>
                <Typography variant="body1" fontWeight="bold">{selected.asset_name || selected.asset_id}</Typography>

                {selected.request_reason && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Motivo</Typography>
                    <Typography variant="body2">{selected.request_reason}</Typography>
                  </>
                )}

                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Solicitante</Typography>
                    <Typography variant="body2">{selected.requested_by_name || selected.requested_by || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data</Typography>
                    <Typography variant="body2">{selected.requested_at ? new Date(selected.requested_at).toLocaleString('pt-BR') : '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2">{statusChip(selected.status).label}</Typography>
                  </Box>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Alterações solicitadas
              </Typography>

              {changesEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">—</Typography>
              ) : (
                <Box>
                  {changesEntries.map(([field, diff]) => (
                    <Box key={field} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {labelForField(field)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmt(diff?.old)} → {fmt(diff?.new)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                label="Observações do revisor (opcional)"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                multiline
                rows={2}
              />

              {selected.review_notes && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Observações anteriores: {selected.review_notes}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} disabled={submitting}>
            Fechar
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="outlined"
            startIcon={submitting ? <CircularProgress size={16} /> : <RejectIcon />}
            disabled={submitting || String(selected?.status || '').toLowerCase() !== 'pending'}
          >
            Recusar
          </Button>
          <Button
            onClick={handleApprove}
            color="success"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <ApproveIcon />}
            disabled={submitting || String(selected?.status || '').toLowerCase() !== 'pending'}
          >
            Aprovar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AssetEditRequests;
